import FuseUtils from '@fuse/utils/FuseUtils';
import { UserDataState, UserModelInDTO } from 'app/auth/store/type';
import { API_V2_ERROR_STATUS, HTTP_FORBIDDEN, HTTP_UNAUTHORIZED } from 'app/constants/http.constant';
import { STORAGE_KEYS } from 'app/constants/storage.constant';
import { adminApi } from 'app/main/apps/admin/api';
import { marketingApi } from 'app/main/apps/automatedMarketing/api';
import { boosterApi } from 'app/main/apps/booster/api';
import { categoriesApiV2 } from 'app/main/apps/categories/api';
import { notifApi } from 'app/main/apps/communicationCampaign/api/campaign.api';
import { franchiseProductApi } from 'app/main/apps/communicationCampaign/api/franchiseProduct.api';
import { crmApi } from 'app/main/apps/crm/api';
import { widgetApi } from 'app/main/apps/dashboards/analytics/api';
import { eMarketingApi } from 'app/main/apps/eMarketing/api';
import { formsApi } from 'app/main/apps/forms/api';
import { connectToFranchiseApi, franchiseApi } from 'app/main/apps/franchises/api';
import { gameApi } from 'app/main/apps/games/api';
import { googleReviewsApi } from 'app/main/apps/googleReviews/api';
import { homeApi } from 'app/main/apps/home/api';
import { listCategoryApiV2 } from 'app/main/apps/listCategory/api';
import { loyaltiesApi } from 'app/main/apps/loyalties/api';
import { pronoApi } from 'app/main/apps/prono/api';
import { referralApi } from 'app/main/apps/referral/api';
import { satisfactionApiV2 } from 'app/main/apps/satisfaction/api';
import { scannerApi, scannerProvider } from 'app/main/apps/scanner/api';
import { storesApiV2, storesProvider } from 'app/main/apps/stores/api';
import { tabletApi, tabletProvider } from 'app/main/apps/tablet/api';
import { ownersManagementApi } from 'app/main/apps/usersManagement/ownersManagement/api';
import { visibilityApi } from 'app/main/apps/visibility/api';
import { webPushSubscriptionApi } from 'app/main/apps/webPush/api';
import { clientsApiV2 } from 'app/main/pages/clients/api';
import { validationApiV2 } from 'app/main/pages/validation/api/validation.api';
import axios from 'axios';
import { serviceV2 } from '../geocodeServices/geocode.api';
import { hereInstance } from '../hereServices/here.api';
import { axiosStripe } from '../stripe/stripe.api';

/**
 * Every BaseApi client. Each one is its own axios instance, so a response
 * interceptor on the global axios does not reach them - which is why a 401 from
 * any of these used to bypass the delegated-session recovery entirely.
 */
const apiClients = [
  notifApi,
  adminApi,
  storesProvider,
  scannerApi,
  scannerProvider,
  tabletApi,
  tabletProvider,
  marketingApi,
  storesApiV2,
  franchiseApi,
  clientsApiV2,
  satisfactionApiV2,
  widgetApi,
  serviceV2,
  loyaltiesApi,
  ownersManagementApi,
  validationApiV2,
  eMarketingApi,
  listCategoryApiV2,
  categoriesApiV2,
  franchiseProductApi,
  crmApi,
  gameApi,
  boosterApi,
  webPushSubscriptionApi,
  pronoApi,
  googleReviewsApi,
  referralApi,
  visibilityApi,
  formsApi,
  homeApi,
];

class JwtService extends FuseUtils.EventEmitter {
  /** Set for the rest of this page's life once a recovery reload is scheduled. */
  isRecoveringDelegatedSession = false;

  init() {
    this.setInterceptors();
    this.handleAuthentication();
  }

  setInterceptors = () => {
    const onUnauthorized = (err: any) => {
      // Optional: a network failure has no response, and a TypeError here would mask it.
      // api-v2 signals an expired session as 403 TOKEN_EXPIRED, not 401.
      const isSessionRejected =
        err.response?.status === HTTP_UNAUTHORIZED ||
        (err.response?.status === HTTP_FORBIDDEN &&
          err.response?.data?.error?.status === API_V2_ERROR_STATUS.TOKEN_EXPIRED);
      if (isSessionRejected && err.config && !err.config.__isRetryRequest) {
        this.handleUnauthorized();
      }
      return Promise.reject(err);
    };

    axios.interceptors.response.use(response => response, onUnauthorized);
    apiClients.forEach(client => client.api.interceptors.response.use(response => response, onUnauthorized));
  };

  /**
   * What a 401 means depends on which token was rejected.
   *
   * While a delegated brand is active it is usually just that brand session expiring -
   * it is short-lived on purpose - and the operator's own token is still valid. Logging
   * them out there would destroy a working session over an expected expiry, so recover
   * once by putting the own token back and letting `Auth.tsx` mint a fresh brand
   * session on boot. The marker makes it one attempt per page lifecycle, so a rejected
   * own token falls through to the real logout instead of reloading forever.
   */
  handleUnauthorized = () => {
    // Several requests are usually in flight when the brand token expires. Once the
    // reload is scheduled the page is on its way out, so later 401s must do nothing
    // rather than fall through to the logout below and wipe the session first.
    if (this.isRecoveringDelegatedSession) {
      return;
    }
    const ownToken = localStorage.getItem(STORAGE_KEYS.OWN_ACCESS_TOKEN);
    if (this.getActiveFranchiseId() && ownToken && !this.hasTriedDelegatedRecovery()) {
      this.isRecoveringDelegatedSession = true;
      this.markDelegatedRecoveryTried();
      localStorage.setItem(STORAGE_KEYS.JWT_ACCESS_TOKEN, ownToken);
      window.location.reload();
      return;
    }
    this.emit('onAutoLogout', 'Invalid access_token');
    this.setSession(null);
    this.removeToken();
  };

  hasTriedDelegatedRecovery = () => {
    try {
      return Boolean(sessionStorage.getItem(STORAGE_KEYS.DELEGATED_RECOVERY));
    } catch {
      // A blocked sessionStorage must not turn a recoverable 401 into a reload loop.
      return true;
    }
  };

  markDelegatedRecoveryTried = () => {
    try {
      sessionStorage.setItem(STORAGE_KEYS.DELEGATED_RECOVERY, 'true');
    } catch {
      // Ignored: the read above already fails closed.
    }
  };

  clearDelegatedRecovery = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEYS.DELEGATED_RECOVERY);
    } catch {
      // Ignored.
    }
  };

  handleAuthentication = () => {
    const id = this.getAccessToken();
    const userId = this.getUserId();
    const data = {
      id,
      userId,
    };

    if (!id) {
      this.emit('onNoAccessToken');
      return;
    }
    if (data.id) {
      this.setSession(data);
      this.setToken(data.id);
      this.emit('onAutoLogin', true);
    } else {
      this.setSession(null);
      this.removeToken();
      this.emit('onAutoLogout', 'access_token expired');
    }
  };

  signInWithEmailAndPassword = (email: string, password: string): Promise<UserModelInDTO> => {
    return new Promise((resolve, reject) => {
      // Through a BaseApi client: the global axios targets lb-api, which has no /api/v2.
      adminApi.api
        .post('api/v2/auth/dashboard/login', {
          email,
          password,
        })
        .then(response => {
          if (response.data.id) {
            this.setSession(response.data);
            this.removeToken();
            this.setToken(response.data.id);
            this.getUserData().then(user => resolve(user));
          }
        })
        .catch(reject);
    });
  };

  forgotPassword = (email: string) => {
    return new Promise((resolve, reject) => {
      adminApi.api
        .post('api/v2/auth/dashboard/password-reset', {
          email,
        })
        .then(resolve)
        .catch(reject);
    });
  };

  signInWithUserId = (): Promise<UserModelInDTO> => {
    return new Promise((resolve: (user: UserDataState) => void, reject) => {
      const userId = this.getUserId();
      const token = this.getAccessToken();
      // /me validates the session, through a BaseApi client because api-v2 reads `?token=`;
      // getUserData's /info then checks the stored user, who is not the token's owner under impersonation.
      adminApi.api
        .get('api/v2/users/me')
        .then(() => {
          if (token) {
            this.setSession({ id: token, userId });
            this.removeToken();
            this.setToken(token);
            return this.getUserData().then(user => {
              resolve({ ...user, displayName: '' });
            });
          }
          this.logout();
          reject(new Error('Failed to login with token.'));
        })
        .catch(() => {
          this.logout();
          reject(new Error('Failed to login with token.'));
        });
    });
  };

  getUserData = () => {
    return new Promise((resolve: (user: UserModelInDTO) => void, reject) => {
      adminApi.api
        .get<UserModelInDTO>(`api/v2/users/${this.getUserId()}/info`)
        .then(response => {
          if (response.data.id) {
            resolve(response.data);
          } else {
            reject(new Error('Could not get user data.'));
          }
        })
        .catch(() => {
          reject(new Error('Could not get user data.'));
        });
    });
  };

  updateUser = (user: any) => {
    return axios.post('/api/auth/user/update', {
      user,
    });
  };

  updateUserData = (user: any, userId: string) => {
    return new Promise((resolve, reject) => {
      axios
        .patch(`api/users/${userId}`, user)
        .then(response => resolve(response.data))
        .catch(() => {
          reject(new Error('Could not update user data: error'));
        });
    });
  };

  createImpostorToken = (userId: string, impostorId: string) => {
    return new Promise((resolve, reject) => {
      adminApi.api
        .post(`api/v2/token/encode`, {
          userId,
          impostorId,
        })
        .then(response => {
          const jwtAccessToken = localStorage.getItem('jwt_access_token');
          if (jwtAccessToken) {
            localStorage.setItem(STORAGE_KEYS.JWT_IMPOSTOR_TOKEN, jwtAccessToken);
          }
          this.setSession({ id: response.data, userId });
          this.removeToken();
          this.setToken(response.data);
          resolve(response.data);
        })
        .catch(() => {
          reject(new Error('Could not create impostor token: error'));
        });
    });
  };

  /**
   * Open a session on another brand, as that brand's franchise owner.
   *
   * The caller's own token is kept aside so coming back needs no round-trip, under
   * keys of its own: the admin impersonation above uses `jwt_impostor_token` for the
   * same job, and an admin impersonating a merchant who then switches brand must not
   * lose their way back to the back-office.
   */
  connectToFranchise = async (franchiseId: string) => {
    const ownToken = this.getAccessToken();
    const ownUserId = this.getUserId();
    const { token, ownerUserId } = await connectToFranchiseApi(franchiseId);

    if (ownToken && ownUserId && !localStorage.getItem(STORAGE_KEYS.OWN_ACCESS_TOKEN)) {
      localStorage.setItem(STORAGE_KEYS.OWN_ACCESS_TOKEN, ownToken);
      localStorage.setItem(STORAGE_KEYS.OWN_USER_ID, ownUserId);
    }
    localStorage.setItem(STORAGE_KEYS.ACTIVE_FRANCHISE_ID, franchiseId);
    this.markBrandPicked();
    localStorage.removeItem(STORAGE_KEYS.CURRENT_STORE_ID);
    localStorage.removeItem(STORAGE_KEYS.SELECTED_TAG_ID);
    localStorage.removeItem(STORAGE_KEYS.START_PAGE_URL);

    this.setSession({ id: token, userId: ownerUserId });
    this.removeToken();
    this.setToken(token);
    this.clearDelegatedRecovery();
    return token;
  };

  /**
   * Put the session back on the brand that was active before a reload.
   *
   * The delegated token is short-lived and is never the source of truth - the
   * caller's own token is, and the brand session is minted again from it on every
   * boot. That is also what makes a revoked grant take effect.
   */
  restoreFranchiseSession = async (franchiseId: string) => {
    const ownToken = localStorage.getItem(STORAGE_KEYS.OWN_ACCESS_TOKEN);
    const ownUserId = localStorage.getItem(STORAGE_KEYS.OWN_USER_ID);
    if (!ownToken || !ownUserId) {
      this.leaveFranchise();
      return;
    }
    this.setSession({ id: ownToken, userId: ownUserId });
    this.removeToken();
    this.setToken(ownToken);
    try {
      await this.connectToFranchise(franchiseId);
    } catch {
      this.leaveFranchise();
    }
  };

  /** Drop a delegated session and hand the caller back their own. */
  leaveFranchise = () => {
    const ownToken = localStorage.getItem(STORAGE_KEYS.OWN_ACCESS_TOKEN);
    const ownUserId = localStorage.getItem(STORAGE_KEYS.OWN_USER_ID);
    localStorage.removeItem(STORAGE_KEYS.ACTIVE_FRANCHISE_ID);
    localStorage.removeItem(STORAGE_KEYS.OWN_ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.OWN_USER_ID);
    localStorage.removeItem(STORAGE_KEYS.CURRENT_STORE_ID);
    localStorage.removeItem(STORAGE_KEYS.SELECTED_TAG_ID);
    localStorage.removeItem(STORAGE_KEYS.START_PAGE_URL);

    if (!ownToken || !ownUserId) {
      return false;
    }
    this.setSession({ id: ownToken, userId: ownUserId });
    this.removeToken();
    this.setToken(ownToken);
    return true;
  };

  getActiveFranchiseId = () => {
    return localStorage.getItem(STORAGE_KEYS.ACTIVE_FRANCHISE_ID);
  };

  markBrandPicked = () => {
    localStorage.setItem(STORAGE_KEYS.BRAND_PICKED, 'true');
  };

  /** Whether this session already answered the brand question, so a reload can skip it. */
  hasPickedBrand = () => {
    return Boolean(
      localStorage.getItem(STORAGE_KEYS.BRAND_PICKED) || localStorage.getItem(STORAGE_KEYS.ACTIVE_FRANCHISE_ID),
    );
  };

  setSession = (data: any) => {
    if (data?.id) {
      localStorage.setItem(STORAGE_KEYS.JWT_ACCESS_TOKEN, data.id);
      localStorage.setItem(STORAGE_KEYS.USER_ID_LB, data.userId);
      axios.defaults.headers.common.Authorization = data.id;
      axiosStripe.defaults.headers.common.Authorization = data.id;
      hereInstance.defaults.headers.common.Authorization = data.id;
    } else {
      localStorage.removeItem(STORAGE_KEYS.JWT_ACCESS_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER_ID_LB);
      localStorage.removeItem(STORAGE_KEYS.SELECTED_TAG_ID);
      localStorage.removeItem(STORAGE_KEYS.START_PAGE_URL);
      localStorage.removeItem(STORAGE_KEYS.JWT_IMPOSTOR_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.CURRENT_STORE_ID);
      localStorage.removeItem(STORAGE_KEYS.ACTIVE_FRANCHISE_ID);
      localStorage.removeItem(STORAGE_KEYS.OWN_ACCESS_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.OWN_USER_ID);
      localStorage.removeItem(STORAGE_KEYS.BRAND_PICKED);
      delete axios.defaults.headers.common.Authorization;
    }
  };

  setToken = (token: string) => {
    apiClients.forEach(client => client.setToken(token));
  };

  removeToken = () => {
    apiClients.forEach(client => client.removeToken());
  };

  logout = () => {
    this.setSession(null);
    this.removeToken();
    localStorage.removeItem('jwt_impostor_token');
  };

  getAccessToken = () => {
    return window.localStorage.getItem('jwt_access_token');
  };

  getUserId = () => {
    return window.localStorage.getItem('user_id_lb');
  };

  getImpostorToken = () => {
    return window.localStorage.getItem('jwt_impostor_token');
  };
}

const instance = new JwtService();

export default instance;
